import React, { useCallback, useEffect, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Row,
  Upload,
  message,
  Descriptions,
  List,
  Avatar,
  Checkbox,
} from "antd";

import { useHistory } from "react-router-dom";

import _ from "lodash";
import moment from "moment";

// Firebase
import firebase from "firebase/compat/app";

import getFileIcon from "../../../../modules/helpers/fileIcon";
import { newOpId, stamp } from "../../../../modules/helpers/audit";
import { normalizeCountry } from "../../../../modules/helpers/countries";

// Pays d'un destinataire : nom canonique + code ISO (liste de référence, décision du 07/10/2026).
// Une valeur hors liste ne devrait plus arriver (formulaire et import la refusent) : elle est
// gardée telle quelle, sans code, et le back-office la signale « pays non reconnu ».
function countryFields(value) {
  const country = normalizeCountry(value);
  return country ? { country: country.name, country_code: country.code } : { country: String(value) };
}

export default function Attachments({ config }) {
  const [accept, setAccept] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [normalizedRecipients, setNormalizedRecipients] = useState([]);

  const history = useHistory();
  const dispatch = useDispatch();

  const formValues = useSelector((state) => state.messagePayload, shallowEqual);
  const user = useSelector((state) => state.user, shallowEqual);

  // Adresse de retour AR : par défaut celle de la société (companyData), modifiable par le client.
  const companyData = user.companyData || {};
  const [customReturn, setCustomReturn] = useState(false);
  const [returnAddress, setReturnAddress] = useState({
    name: companyData.name || "",
    address_line_1: companyData.address_line_1 || "",
    address_line_2: companyData.address_line_2 || "",
    postal_code: companyData.postal_code || "",
    city: companyData.city || "",
  });

  useEffect(() => {
    setNormalizedRecipients(
      _.uniqBy(
        [..._.flatMap(formValues.recipients.lists, "contacts"), ...formValues.recipients.contacts],
        "key"
      )
    );
  }, [formValues.recipients]);

  // Configure Firestore
  const db = firebase.firestore();

  function clickBack() {
    history.goBack();
  }

  return (
    <>
      {/*<div>{JSON.stringify(formValues)}</div>*/}
      {config.printed && (
        <Descriptions bordered title="Options d'impression" size="small" column={6}>
          <Descriptions.Item label="Paramètre couleur" span={3}>
            {formValues.color_option === "black_white" ? "Noir/Blanc" : "Couleur"}
          </Descriptions.Item>
          <Descriptions.Item label="Recto ou Recto/Verso" span={3}>
            {formValues.face_option === "one_face" ? "Recto seulement" : "Recto/Verso"}
          </Descriptions.Item>
          {formValues.recipients_type && (
            <Descriptions.Item label="Type de destinataires" span={3}>
              {formValues.recipients_type === "unique"
                ? "Destinataire unique"
                : "Destinataires multiples"}
            </Descriptions.Item>
          )}
          {config.type === "simple" ? (
            <Descriptions.Item label="Délai de distribution" span={3}>
              {formValues.envelope}
            </Descriptions.Item>
          ) : (
            <>
              <Descriptions.Item label="Accusé de réception" span={3}>
                {formValues.receipt_aknowledge === "yes"
                  ? "Avec accusé de réception"
                  : "Sans accusé de réception"}
              </Descriptions.Item>
              <Descriptions.Item label="Niveau de recommandation" span={6}>
                {formValues.level}
              </Descriptions.Item>
            </>
          )}
        </Descriptions>
      )}

      {config.type === "electronique" && (
        <>
          <Descriptions
            bordered
            style={config.printed ? { marginTop: 32 } : {}}
            title="Options d'envoi"
            size="small"
            column={4}
          >
            <Descriptions.Item label="Type d'envoi" span={formValues.date_type === "now" ? 4 : 2}>
              {formValues.date_type === "now" ? "Immédiat" : "Différé"}
            </Descriptions.Item>
            {formValues.date_type !== "now" && formValues.date && (
              <Descriptions.Item label="Date d'envoi" span={2}>
                <span style={{ textTransform: "capitalize" }}>
                  {formValues.date.format("dddd D MMMM YYYY")}
                </span>
                &nbsp;({formValues.date.fromNow()})
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Référence 1" span={2}>
              {formValues.ref1 || "Aucune"}
            </Descriptions.Item>
            <Descriptions.Item label="Référence 2" span={2}>
              {formValues.ref2 || "Aucune"}
            </Descriptions.Item>
          </Descriptions>

          <Descriptions style={{ marginTop: 32 }} title="Destinataires" />
          <Card type="inner" style={{ width: "100%" }} bodyStyle={{ padding: "8px 24px 0" }}>
            <Row gutter={[8, 8]}>
              <Col span={24}>
                <List
                  itemLayout="horizontal"
                  dataSource={normalizedRecipients}
                  renderItem={(item) => (
                    <List.Item>
                      <List.Item.Meta
                        title={`${item.first_name} ${item.last_name}`}
                        description={
                          <div>
                            <div>
                              {item.type === "individual" ? "Particulier" : "Professionnel"}
                            </div>
                            {config.printed ? (
                              <>
                                <div>{item.address_line_1}</div>
                                {item.address_line_2 && <div>{item.address_line_2}</div>}
                                <div>
                                  {item.city}, {item.postal_code}
                                </div>
                              </>
                            ) : (
                              <div>{item.email}</div>
                            )}
                          </div>
                        }
                      />
                    </List.Item>
                  )}
                />
              </Col>
            </Row>
          </Card>
        </>
      )}

      {config.type === "recommande" && normalizedRecipients.length > 0 && (
        <>
          <Descriptions style={{ marginTop: 32 }} title={`Destinataires (${normalizedRecipients.length})`} />
          <Card type="inner" style={{ width: "100%" }} bodyStyle={{ padding: "8px 24px 0" }}>
            <Row gutter={[8, 8]}>
              <Col span={24}>
                <List
                  itemLayout="horizontal"
                  dataSource={normalizedRecipients}
                  renderItem={(item) => (
                    <List.Item>
                      <List.Item.Meta
                        title={`${item.first_name} ${item.last_name}`}
                        description={
                          <div>
                            <div>{item.address_line_1}</div>
                            {item.address_line_2 && <div>{item.address_line_2}</div>}
                            <div>{item.postal_code} {item.city}</div>
                          </div>
                        }
                      />
                    </List.Item>
                  )}
                />
              </Col>
            </Row>
          </Card>
        </>
      )}

      {config.type === "recommande" && (
        <>
          <Descriptions style={{ marginTop: 32 }} title="Adresse de retour" />
          <Card type="inner" style={{ width: "100%" }} bodyStyle={{ padding: "16px 24px" }}>
            <Checkbox
              checked={customReturn}
              onChange={(e) => setCustomReturn(e.target.checked)}
            >
              Personnaliser l&apos;adresse de retour
            </Checkbox>
            {!customReturn ? (
              <div style={{ marginTop: 8, color: "rgba(0,0,0,0.65)" }}>
                Par défaut, l&apos;adresse de retour des accusés de réception est celle de votre
                entreprise{returnAddress.name ? ` (${returnAddress.name})` : ""}.
              </div>
            ) : (
              <Row gutter={[8, 8]} style={{ marginTop: 12 }}>
                <Col span={24}>
                  <Input
                    placeholder="Nom / raison sociale"
                    value={returnAddress.name}
                    onChange={(e) => setReturnAddress({ ...returnAddress, name: e.target.value })}
                  />
                </Col>
                <Col span={24}>
                  <Input
                    placeholder="Adresse (ligne 1)"
                    value={returnAddress.address_line_1}
                    onChange={(e) =>
                      setReturnAddress({ ...returnAddress, address_line_1: e.target.value })
                    }
                  />
                </Col>
                <Col span={24}>
                  <Input
                    placeholder="Adresse (ligne 2, optionnel)"
                    value={returnAddress.address_line_2}
                    onChange={(e) =>
                      setReturnAddress({ ...returnAddress, address_line_2: e.target.value })
                    }
                  />
                </Col>
                <Col span={8}>
                  <Input
                    placeholder="Code postal"
                    value={returnAddress.postal_code}
                    onChange={(e) =>
                      setReturnAddress({ ...returnAddress, postal_code: e.target.value })
                    }
                  />
                </Col>
                <Col span={16}>
                  <Input
                    placeholder="Ville"
                    value={returnAddress.city}
                    onChange={(e) => setReturnAddress({ ...returnAddress, city: e.target.value })}
                  />
                </Col>
              </Row>
            )}
          </Card>
        </>
      )}

      <Descriptions
        style={{ marginTop: 32 }}
        title={!config.printed ? "Pièces jointes" : "Documents"}
      />
      <Card type="inner" style={{ width: "100%" }} bodyStyle={{ padding: "8px 24px 0" }}>
        <Row gutter={[8, 8]}>
          <Col span={24}>
            <List
              itemLayout="horizontal"
              dataSource={
                formValues.attachments.length
                  ? formValues.attachments
                  : [{ filename: "Aucune pièce jointe" }]
              }
              renderItem={(item) => (
                <List.Item>
                  <List.Item.Meta
                    avatar={formValues.attachments.length ? getFileIcon(item.mimetype) : null}
                    title={item.filename}
                    description={
                      formValues.attachments.length ? (
                        <>
                          {config.printed && item.pages && (
                            <span style={{ marginRight: 16 }}>
                              {item.pages} page{item.pages > 1 ? "s" : ""}
                            </span>
                          )}
                          <a href={item.url} rel="noopener noreferrer" target="_blank">
                            Ouvrir le fichier
                          </a>
                        </>
                      ) : null
                    }
                  />
                </List.Item>
              )}
            />
          </Col>
        </Row>
      </Card>

      {!config.printed && (
        <Descriptions
          bordered
          title="Contenu du message"
          size="small"
          column={4}
          style={{ marginTop: 32 }}
        >
          <Descriptions.Item label="Objet" span={4}>
            {formValues.subject}
          </Descriptions.Item>
          <Descriptions.Item label="Message" span={4} style={{ whiteSpace: "pre-line" }}>
            {formValues.content}
          </Descriptions.Item>
        </Descriptions>
      )}

      <Row gutter={[8, 8]} style={{ marginTop: 16 }}>
        <Col span={24} style={{ textAlign: "right" }}>
          <Checkbox
            onChange={(e) => {
              setAccept(e.target.checked);
            }}
          >
            Je confirme avoir bien pris connaissance des informations et des{" "}
            <a target="_blank" href="https://s3.eu-west-3.amazonaws.com/www.solidoc.fr/cgv.pdf">
              conditions générales de vente
            </a>
          </Checkbox>
        </Col>
        <Col span={24} style={{ textAlign: "right" }}>
          <Form.Item>
            <Button disabled={isSubmitting} onClick={clickBack} style={{ marginRight: "8px" }}>
              Précédent
            </Button>
            <Button
              disabled={!accept}
              type="primary"
              htmlType="submit"
              loading={isSubmitting}
              onClick={() => {
                setIsSubmitting(true);

                const initialStatus = formValues.date_type === "now" ? "prepare" : "scheduled";
                // Un identifiant d'opération relie l'envoi et ses destinataires dans le journal
                const op = newOpId();

                const payload = {
                  ..._.pick(formValues, config.pickedKeys),
                  // For paper sends, leave pages and addresses_count empty for operator to fill
                  // For electronic sends, calculate pages from attachments
                  pages: config.printed
                    ? null
                    : formValues.attachments.reduce((total, a) => total + (a.pages || 1), 0) || 1,
                  addresses_count: config.printed ? null : undefined,
                  type: config.type,
                  user: user.userRef,
                  created_at: firebase.firestore.FieldValue.serverTimestamp(),
                  date:
                    formValues.date_type === "now"
                      ? firebase.firestore.FieldValue.serverTimestamp()
                      : formValues.date &&
                        firebase.firestore.Timestamp.fromDate(formValues.date.toDate()),
                  last_change: stamp({ op }),
                };

                user.company
                  .collection("messages")
                  .add(payload)
                  .then((messageRef) => {
                    const batch = db.batch();

                    let recipients = [];

                    if (config.type === "electronique") {
                      recipients = _.map(normalizedRecipients, (contact) => {
                        const cloned = _.clone(contact);

                        cloned.last_updated = firebase.firestore.FieldValue.serverTimestamp();
                        cloned.status = initialStatus;
                        cloned.company = user.company;
                        cloned.message = messageRef;
                        cloned.created_at = firebase.firestore.FieldValue.serverTimestamp();
                        cloned.user = user.userRef;
                        cloned.send_date =
                          formValues.date_type === "now"
                            ? null
                            : formValues.date &&
                              firebase.firestore.Timestamp.fromDate(formValues.date.toDate());

                        return cloned;
                      });
                    } else if (config.type === "recommande" && normalizedRecipients.length > 0) {
                      // Recommandé avec destinataires individuels
                      recipients = _.map(normalizedRecipients, (contact) => {
                        return {
                          first_name: contact.first_name || "",
                          last_name: contact.last_name || "",
                          address_line_1: contact.address_line_1 || "",
                          address_line_2: contact.address_line_2 || "",
                          postal_code: contact.postal_code || "",
                          city: contact.city || "",
                          ...countryFields(contact.country),
                          type: contact.type || "individual",
                          company_name: contact.company_name || "",
                          reference: contact.reference || "",
                          // Adresse de retour, posée dès la création :
                          // custom client ("personnaliser") > adresse du contact > adresse
                          // de la société > vide (→ génération bloquée si tout est vide).
                          return_address_name:
                            (customReturn && returnAddress.name) ||
                            contact.return_address_name ||
                            companyData.name ||
                            "",
                          return_address_line_1:
                            (customReturn && returnAddress.address_line_1) ||
                            contact.return_address_line_1 ||
                            companyData.address_line_1 ||
                            "",
                          return_address_line_2:
                            (customReturn && returnAddress.address_line_2) ||
                            contact.return_address_line_2 ||
                            companyData.address_line_2 ||
                            "",
                          return_address_postal_code:
                            (customReturn && returnAddress.postal_code) ||
                            contact.return_address_postal_code ||
                            companyData.postal_code ||
                            "",
                          return_address_city:
                            (customReturn && returnAddress.city) ||
                            contact.return_address_city ||
                            companyData.city ||
                            "",
                          last_updated: firebase.firestore.FieldValue.serverTimestamp(),
                          status: initialStatus,
                          company: user.company,
                          message: messageRef,
                          created_at: firebase.firestore.FieldValue.serverTimestamp(),
                          user: user.userRef,
                          send_date: null,
                          send_type: config.type,
                        };
                      });
                    } else {
                      // Simple ou recommandé legacy (sans destinataires)
                      recipients.push({
                        last_updated: firebase.firestore.FieldValue.serverTimestamp(),
                        status: initialStatus,
                        company: user.company,
                        message: messageRef,
                        created_at: firebase.firestore.FieldValue.serverTimestamp(),
                        user: user.userRef,
                        send_date: null,
                        send_type: config.type,
                      });
                    }

                    // Tampon posé en dernier : remplace celui d'un contact copié du carnet
                    recipients.forEach((recipient) => {
                      batch.set(messageRef.collection("recipients").doc(), {
                        ...recipient,
                        last_change: stamp({ op }),
                      });
                    });

                    return batch.commit().then(() => {
                      const b64 = btoa(
                        formValues.date_type === "now"
                          ? messageRef.id
                          : `${messageRef.id}:${formValues.date.format("YYYY-MM-DD")}`
                      );

                      dispatch({ type: "RESET_FORM_VALUES" });
                      history.push(`/envoi/${config.type}/success/${b64}`);
                    });
                  })
                  .catch((e) => {
                    console.error(e);
                    setIsSubmitting(false);
                    message.error("L'envoi n'a pas pu être enregistré. Veuillez réessayer.");
                  });
              }}
            >
              Envoyer
            </Button>
          </Form.Item>
        </Col>
      </Row>
    </>
  );
}
